/**
 * Tabs — a tab list over panels (WAI-ARIA tabs pattern, automatic activation).
 *
 *   <Tabs value={tab} onValueChange={setTab}>   // or defaultValue
 *     <TabsList aria-label="Gateway">
 *       <TabsTrigger value="overview">Overview</TabsTrigger>
 *       <TabsTrigger value="devices" badge={3}>Devices</TabsTrigger>
 *     </TabsList>
 *     <TabsContent value="overview">…</TabsContent>
 *   </Tabs>
 *
 * Arrow Left / Right move between tabs (and select), Home / End jump to the
 * ends; only the selected tab is in the tab order. Inactive panels are not
 * rendered, so a heavy panel (live values polling) runs only while shown,
 * unless the panel says `keepMounted`: a form with a draft stays in the tree,
 * hidden, so switching away and back loses nothing.
 *
 * No Radix dependency (the app does not ship @radix-ui/react-tabs); ids come
 * from useId so it renders under renderToStaticMarkup in tests.
 */

import {
  createContext,
  useContext,
  useId,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { cn } from "./cn";

interface TabsCtx {
  value: string;
  select: (v: string) => void;
  base: string;
}

const Ctx = createContext<TabsCtx | null>(null);

function useTabs(): TabsCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("Tabs parts must be inside <Tabs>");
  return c;
}

const safe = (v: string) => v.replace(/[^A-Za-z0-9_-]/g, "_");

export interface TabsProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children: ReactNode;
}

export function Tabs({ value, defaultValue = "", onValueChange, className, children }: TabsProps) {
  const [state, setState] = useState(defaultValue);
  const base = useId();
  const current = value ?? state;
  const select = (v: string) => {
    if (value === undefined) setState(v);
    onValueChange?.(v);
  };
  return (
    <Ctx.Provider value={{ value: current, select, base }}>
      <div className={cn("flex flex-col", className)}>{children}</div>
    </Ctx.Provider>
  );
}

/** Roving focus inside a tab list: arrows move to the next enabled tab and select it. */
function rovingKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
  const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
  if (!keys.includes(e.key)) return;
  const list = e.currentTarget.closest('[role="tablist"]');
  if (!list) return;
  const tabs = Array.from(list.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])'));
  const i = tabs.indexOf(e.currentTarget);
  if (tabs.length === 0) return;
  e.preventDefault();
  const next =
    e.key === "Home"
      ? 0
      : e.key === "End"
        ? tabs.length - 1
        : (i + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
  tabs[next].focus();
  tabs[next].click();
}

export function TabsList({
  className,
  children,
  "aria-label": ariaLabel,
}: {
  className?: string;
  children: ReactNode;
  "aria-label"?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn("flex items-center gap-1 overflow-x-auto border-b border-border", className)}
    >
      {children}
    </div>
  );
}

export function TabsTrigger({
  value,
  disabled,
  badge,
  className,
  children,
}: {
  value: string;
  disabled?: boolean;
  /** A small count or marker after the label. */
  badge?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const t = useTabs();
  const selected = t.value === value;
  return (
    <button
      onKeyDown={rovingKeyDown}
      type="button"
      role="tab"
      id={`${t.base}-tab-${safe(value)}`}
      aria-selected={selected}
      aria-controls={`${t.base}-panel-${safe(value)}`}
      tabIndex={selected ? 0 : -1}
      disabled={disabled}
      onClick={() => t.select(value)}
      className={cn(
        "-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
        selected
          ? "border-brand text-on-surface"
          : "border-transparent text-on-surface-muted hover:text-on-surface",
        className,
      )}
    >
      {children}
      {badge !== undefined && badge !== null && badge !== false && (
        <span className="rounded-full bg-surface-muted px-1.5 text-[10px] font-medium text-on-surface-muted">
          {badge}
        </span>
      )}
    </button>
  );
}

export function TabsContent({
  value,
  className,
  keepMounted,
  children,
}: {
  value: string;
  className?: string;
  /**
   * Keep the panel in the tree while another tab is selected, hidden (`hidden`, so out of sight, out of the tab order
   * and out of the accessibility tree) instead of unmounted. For a panel that holds a draft or an upload in flight.
   */
  keepMounted?: boolean;
  children: ReactNode;
}) {
  const t = useTabs();
  const selected = t.value === value;
  if (!selected && !keepMounted) return null;
  return (
    <div
      role="tabpanel"
      id={`${t.base}-panel-${safe(value)}`}
      aria-labelledby={`${t.base}-tab-${safe(value)}`}
      hidden={!selected}
      tabIndex={selected ? 0 : undefined}
      className={cn("focus-visible:outline-none", className)}
    >
      {children}
    </div>
  );
}
